import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import SimpleBar from "simplebar-react";
import { useCookies } from "react-cookie";
import {
  Badge,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  UncontrolledDropdown,
} from "reactstrap";
import { Icon, UserAvatar } from "../../../components/Component";
import getTextColor from "../../../components/custom/getTextColor";
import { serviceGetUsersSalesAndCsByDeparmentId } from "../../../services/users";
import { listTimeLabel, mediaPreviewOf } from "./livechatState";

export const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "UNOPENED", label: "Unopened" },
  { value: "OPENED", label: "Opened" },
  { value: "PENDING", label: "Pending" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "SOLVED", label: "Solved" },
];

const SEARCH_DELAY_MS = 300;
const LOAD_MORE_OFFSET_PX = 150;

export const visitorName = (conversation) => conversation?.contact?.name || "Pengunjung belum terverifikasi";

const STATUS_COLORS = {
  RESOLVED: "success",
  SOLVED: "info",
  OPENED: "primary",
  PENDING: "warning",
  UNOPENED: "danger",
};

const holderName = (conversation) =>
  conversation.user
    ? `${conversation.user.firstName ?? ""} ${conversation.user.lastName ?? ""}`.trim()
    : "Belum di-assign";

const ConversationItem = ({ conversation, isSelected, options, onSelect }) => {
  const department = options.departments[conversation.departmentId];
  const site = options.channels[conversation.channelId];
  const isUnread = conversation.unreadCount > 0;
  const media = mediaPreviewOf(conversation.lastMessagePreview);

  return (
    <li className={`chat-item lc-item w-100 ${isSelected ? "is-selected" : ""}`}>
      <a
        className="chat-link"
        href={`?c=${conversation.conversationId}`}
        onClick={(ev) => {
          ev.preventDefault();
          onSelect(conversation.conversationId);
        }}
      >
        <UserAvatar icon="user-alt-fill" className="chat-media overflow-hidden" />
        <div className="chat-info">
          <div className="chat-from">
            <div className="name text-truncate pr-2">{visitorName(conversation)}</div>
            <span className="time">{listTimeLabel(conversation.lastMessageAt)}</span>
          </div>
          <div className="chat-context custom-chat-context">
            <div className="text">
              <p className={`text-truncate pr-2 lc-preview ${isUnread ? "is-unread" : "font-weight-light"}`}>
                {media && <Icon name={media.icon} className="text-soft mr-1" style={{ fontSize: 13 }} />}
                {media ? media.label : (conversation.lastMessagePreview ?? "")}
              </p>
            </div>
            <div className="position-relative">
              <Badge color={`outline-${STATUS_COLORS[conversation.status] ?? "primary"}`} pill>
                {conversation.status}
              </Badge>
              {isUnread && (
                <div className="unread-count position-absolute d-flex">
                  <span>{conversation.unreadCount}</span>
                </div>
              )}
            </div>
          </div>
          <SimpleBar className="nk-chat-aside-body py-1">
            <div className="badge-list d-flex" style={{ gap: 4 }}>
              {department && (
                <span
                  className="badge rounded-pill"
                  style={{ background: department.color, color: getTextColor(department.color) }}
                >
                  {department.name}
                </span>
              )}
              {site && <span className="badge rounded-pill badge-dark">{site.name}</span>}
              <span className="badge rounded-pill lc-holder">{holderName(conversation)}</span>
              {conversation.authStatus !== "logged_in" && <span className="badge rounded-pill lc-holder">Tamu</span>}
            </div>
          </SimpleBar>
        </div>
      </a>
    </li>
  );
};

const FilterButton = ({ icon, title, isActive, children }) => (
  <UncontrolledDropdown>
    <DropdownToggle
      tag="button"
      type="button"
      title={title}
      aria-label={title}
      className={`btn btn-round btn-icon btn-light dropdown-toggle ${isActive ? "lc-filter-on" : ""}`}
    >
      <Icon name={icon} />
    </DropdownToggle>
    <DropdownMenu right className="lc-filter-menu">
      {children}
    </DropdownMenu>
  </UncontrolledDropdown>
);

const FilterOptions = ({ items, value, onChange, allLabel }) => (
  <ul className="link-list-opt no-bdr">
    <li>
      <DropdownItem
        tag="a"
        href="#all"
        active={!value}
        onClick={(ev) => {
          ev.preventDefault();
          onChange(undefined);
        }}
      >
        <span>{allLabel}</span>
      </DropdownItem>
    </li>
    {items.map((item) => (
      <li key={item.id}>
        <DropdownItem
          tag="a"
          href={`#${item.id}`}
          active={value === item.id}
          onClick={(ev) => {
            ev.preventDefault();
            onChange(item.id);
          }}
        >
          <span>{item.name}</span>
        </DropdownItem>
      </li>
    ))}
  </ul>
);

const LivechatAside = ({ conversations, hasMore, isLoading, selectedId, me, filters, options, ...handlers }) => {
  const { onFiltersChange, onLoadMore, onSelect } = handlers;
  const [cookies] = useCookies(["SIDikey"]);
  const [search, setSearch] = useState("");
  const [sales, setSales] = useState([]);
  const debounceRef = useRef(null);

  const setFilter = (key, value) => onFiltersChange((prev) => ({ ...prev, [key]: value }));
  const departmentIds = filters.departmentId ? [filters.departmentId] : me.departmentIds;

  useEffect(() => {
    if (me.access === "own" || departmentIds.length === 0) return;

    serviceGetUsersSalesAndCsByDeparmentId(cookies.SIDikey, departmentIds.join(",")).then((users) =>
      setSales(Array.isArray(users) ? users : []),
    );
  }, [cookies.SIDikey, me.access, departmentIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const onSearch = (value) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setFilter("q", value.trim() || undefined), SEARCH_DELAY_MS);
  };

  const onDate = (key, value, edge) => setFilter(key, value ? moment(value)[edge]("day").toISOString() : undefined);

  const onScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (hasMore && !isLoading && scrollHeight - scrollTop - clientHeight < LOAD_MORE_OFFSET_PX) onLoadMore();
  };

  const unopenedCount = conversations.filter((conversation) => conversation.status === "UNOPENED").length;
  const salesItems = sales.map((user) => ({
    id: user.id,
    name: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
  }));

  return (
    <div className={`nk-chat-aside ${selectedId ? "has-aside" : ""}`}>
      <SimpleBar className="w-100">
        <div className="d-flex w-100" style={{ height: 40 }}>
          <Nav tabs style={{ border: "none", flexWrap: "nowrap" }}>
            {STATUS_TABS.map((tab, index) => (
              <NavItem key={tab.value || "all"}>
                <NavLink
                  tag="button"
                  type="button"
                  className={`btn btn-link lc-tab ${(filters.status ?? "") === tab.value ? "is-active" : ""}`}
                  style={{ marginLeft: index === 0 ? 10 : 0, marginRight: index === STATUS_TABS.length - 1 ? 10 : 0 }}
                  onClick={() => setFilter("status", tab.value || undefined)}
                >
                  {tab.label}
                  {tab.value === "" && <span className="ml-1">({unopenedCount})</span>}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>
      </SimpleBar>

      <div className="nk-chat-aside-search p-2 lc-search">
        <div className="form-group mb-0">
          <div className="form-control-wrap d-flex" style={{ gap: 4 }}>
            <div className="form-icon form-icon-left">
              <Icon name="search" />
            </div>
            <Input
              type="text"
              className="form-round"
              placeholder="Cari nama atau nomor"
              aria-label="Cari nama atau nomor"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
            />
            {me.access === "all" && (
              <FilterButton icon="building" title="Filter department" isActive={Boolean(filters.departmentId)}>
                <FilterOptions
                  items={Object.values(options.departments)}
                  value={filters.departmentId}
                  onChange={(value) => setFilter("departmentId", value)}
                  allLabel="Semua department"
                />
              </FilterButton>
            )}
            {me.access !== "own" && (
              <FilterButton icon="user-list" title="Filter sales" isActive={Boolean(filters.userId)}>
                <FilterOptions
                  items={salesItems}
                  value={filters.userId}
                  onChange={(value) => setFilter("userId", value)}
                  allLabel="Semua sales"
                />
              </FilterButton>
            )}
            <FilterButton icon="globe" title="Filter situs" isActive={Boolean(filters.channelId)}>
              <FilterOptions
                items={Object.values(options.channels)}
                value={filters.channelId}
                onChange={(value) => setFilter("channelId", value)}
                allLabel="Semua situs"
              />
            </FilterButton>
            <FilterButton icon="filter-alt" title="Filter tag" isActive={Boolean(filters.tagId)}>
              <FilterOptions
                items={options.tags}
                value={filters.tagId}
                onChange={(value) => setFilter("tagId", value)}
                allLabel="Semua tag"
              />
            </FilterButton>
            <FilterButton icon="calendar" title="Filter tanggal" isActive={Boolean(filters.dateFrom || filters.dateTo)}>
              <div className="p-2">
                <label className="form-label small mb-1" htmlFor="lc-date-from">
                  Dari
                </label>
                <Input
                  id="lc-date-from"
                  type="date"
                  bsSize="sm"
                  className="mb-2"
                  onChange={(e) => onDate("dateFrom", e.target.value, "startOf")}
                />
                <label className="form-label small mb-1" htmlFor="lc-date-to">
                  Sampai
                </label>
                <Input
                  id="lc-date-to"
                  type="date"
                  bsSize="sm"
                  onChange={(e) => onDate("dateTo", e.target.value, "endOf")}
                />
              </div>
            </FilterButton>
          </div>
        </div>
      </div>

      <SimpleBar className="nk-chat-aside-body" scrollableNodeProps={{ onScroll }}>
        <div className="nk-chat-list">
          <ul className="chat-list">
            {conversations.map((conversation) => (
              <ConversationItem
                key={conversation.conversationId}
                conversation={conversation}
                isSelected={conversation.conversationId === selectedId}
                options={options}
                onSelect={onSelect}
              />
            ))}
          </ul>
          {isLoading && (
            <div className="d-flex justify-content-center py-3">
              <Spinner size="sm" color="primary" />
            </div>
          )}
          {!isLoading && conversations.length === 0 && (
            <p className="m-3 text-muted text-center">
              {filters.q ? "Tidak ada percakapan yang cocok" : "Belum ada percakapan"}
            </p>
          )}
        </div>
      </SimpleBar>
    </div>
  );
};

export default LivechatAside;
